import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Card } from '@mono/ui-components';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { useState } from 'react';
import { ComplianceStatus, ObligationStrength, RequirementCategory } from '../../../types/proposal';
const obligationColors = {
    [ObligationStrength.SHALL]: 'bg-red-100 text-red-700 border-red-300',
    [ObligationStrength.SHALL_NOT]: 'bg-red-100 text-red-700 border-red-300',
    [ObligationStrength.MUST]: 'bg-red-100 text-red-700 border-red-300',
    [ObligationStrength.MUST_NOT]: 'bg-red-100 text-red-700 border-red-300',
    [ObligationStrength.WILL]: 'bg-orange-100 text-orange-700 border-orange-300',
    [ObligationStrength.WILL_NOT]: 'bg-orange-100 text-orange-700 border-orange-300',
    [ObligationStrength.SHOULD]: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    [ObligationStrength.SHOULD_NOT]: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    [ObligationStrength.MAY]: 'bg-gray-100 text-gray-600 border-gray-300',
    [ObligationStrength.MAY_NOT]: 'bg-gray-100 text-gray-600 border-gray-300'
};
const STATUS_COLORS = {
    [ComplianceStatus.NOT_STARTED]: 'bg-gray-100 text-gray-700',
    [ComplianceStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-700',
    [ComplianceStatus.ADDRESSED]: 'bg-green-100 text-green-700',
    [ComplianceStatus.PARTIAL]: 'bg-yellow-100 text-yellow-700',
    [ComplianceStatus.NON_COMPLIANT]: 'bg-red-100 text-red-700',
    [ComplianceStatus.NOT_APPLICABLE]: 'bg-gray-100 text-gray-500'
};
const STATUS_LABELS = {
    [ComplianceStatus.NOT_STARTED]: 'Not Started',
    [ComplianceStatus.IN_PROGRESS]: 'In Progress',
    [ComplianceStatus.ADDRESSED]: 'Addressed',
    [ComplianceStatus.PARTIAL]: 'Partial',
    [ComplianceStatus.NON_COMPLIANT]: 'Non-Compliant',
    [ComplianceStatus.NOT_APPLICABLE]: 'N/A'
};
const RESPONSE_ACTION_LABELS = {
    WRITE_APPROACH: 'Write Approach',
    PROVIDE_EVIDENCE: 'Provide Evidence',
    COMPLY_FORMAT: 'Comply Format',
    SUBMIT_ACTION: 'Submit Action',
    ACKNOWLEDGE: 'Acknowledge',
    PRICE: 'Price',
    STAFF: 'Staff'
};
const RESPONSE_ACTION_COLORS = {
    WRITE_APPROACH: 'bg-purple-100 text-purple-800',
    PROVIDE_EVIDENCE: 'bg-amber-100 text-amber-800',
    COMPLY_FORMAT: 'bg-cyan-100 text-cyan-800',
    SUBMIT_ACTION: 'bg-indigo-100 text-indigo-800',
    ACKNOWLEDGE: 'bg-gray-100 text-gray-800',
    PRICE: 'bg-green-100 text-green-800',
    STAFF: 'bg-pink-100 text-pink-800'
};
const DELIVERABLE_TYPE_LABELS = {
    PROPOSAL: 'P',
    CONTRACT: 'C',
    BOTH: 'B'
};
const DELIVERABLE_TYPE_TITLES = {
    PROPOSAL: 'Proposal only',
    CONTRACT: 'Contract only',
    BOTH: 'Proposal & Contract'
};
const CATEGORY_LABELS = {
    [RequirementCategory.PROPOSAL_INSTRUCTION]: 'Proposal Instruction',
    [RequirementCategory.FORMAT_REQUIREMENT]: 'Format',
    [RequirementCategory.SUBMISSION_REQUIREMENT]: 'Submission',
    [RequirementCategory.SCOPE_REQUIREMENT]: 'Scope',
    [RequirementCategory.PERFORMANCE_STANDARD]: 'Performance',
    [RequirementCategory.DELIVERABLE]: 'Deliverable',
    [RequirementCategory.EVALUATION_CRITERION]: 'Evaluation',
    [RequirementCategory.QUALIFICATION]: 'Qualification',
    [RequirementCategory.CLAUSE_FLOWDOWN]: 'Clause Flowdown',
    [RequirementCategory.COMPLIANCE_REQUIREMENT]: 'Compliance',
    [RequirementCategory.REPORTING_REQUIREMENT]: 'Reporting',
    [RequirementCategory.KEY_PERSONNEL]: 'Key Personnel',
    [RequirementCategory.PERIOD_OF_PERFORMANCE]: 'Period of Performance',
    [RequirementCategory.PLACE_OF_PERFORMANCE]: 'Place of Performance',
    [RequirementCategory.TASK_REQUIREMENT]: 'Task',
    [RequirementCategory.SECURITY_REQUIREMENT]: 'Security',
    [RequirementCategory.CLIN]: 'CLIN',
    [RequirementCategory.OTHER]: 'Other'
};
/**
 * Individual requirement card displaying a compliance requirement
 * with expandable full text, source citation, status, category badges,
 * and enrichment data (entities, related CLINs, section context)
 */ export const RequirementCard = ({ requirement, onClick })=>{
    const [isExpanded, setIsExpanded] = useState(false);
    const displayText = requirement.source.exactText;
    const truncatedText = displayText.length > 200 && !isExpanded ? `${displayText.substring(0, 200)}...` : displayText;
    const handleExpand = ()=>{
        setIsExpanded(!isExpanded);
    };
    const statusColor = STATUS_COLORS[requirement.status] || 'bg-gray-100 text-gray-700';
    const statusLabel = STATUS_LABELS[requirement.status] || requirement.status;
    // Use categories[] for multi-category display, fall back to single category
    const displayCategories = requirement.categories && requirement.categories.length > 0 ? requirement.categories : [
        requirement.category
    ];
    // Source path: prefer sectionContext.fullPath, fall back to "Section X.Y.Z"
    const sourcePath = requirement.sectionContext?.fullPath || (requirement.source.sectionNumber ? `Section ${requirement.source.sectionNumber}` : undefined);
    return /*#__PURE__*/ _jsx(Card, {
        className: "p-4 hover:shadow-md transition-shadow cursor-pointer",
        onClick: ()=>onClick?.(requirement),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-start justify-between gap-4",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex-1 space-y-3",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2 flex-wrap",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-xs font-mono text-gray-500",
                                    children: requirement.requirementId
                                }),
                                /*#__PURE__*/ _jsx(Badge, {
                                    variant: "secondary",
                                    className: `text-xs ${statusColor}`,
                                    children: statusLabel
                                }),
                                displayCategories.map((cat)=>/*#__PURE__*/ _jsx(Badge, {
                                        variant: "outline",
                                        className: "text-xs bg-gray-50 text-gray-600 border-gray-300",
                                        children: CATEGORY_LABELS[cat] || cat
                                    }, cat)),
                                requirement.obligationStrength && /*#__PURE__*/ _jsx(Badge, {
                                    variant: "outline",
                                    className: `text-xs font-medium border ${obligationColors[requirement.obligationStrength]}`,
                                    children: requirement.obligationStrength
                                }),
                                requirement.responseAction && /*#__PURE__*/ _jsx(Badge, {
                                    variant: "secondary",
                                    className: `text-xs font-medium ${RESPONSE_ACTION_COLORS[requirement.responseAction] || 'bg-gray-100 text-gray-800'}`,
                                    children: RESPONSE_ACTION_LABELS[requirement.responseAction] || requirement.responseAction
                                }),
                                requirement.deliverableType && /*#__PURE__*/ _jsx("span", {
                                    className: "inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-600",
                                    title: DELIVERABLE_TYPE_TITLES[requirement.deliverableType] || requirement.deliverableType,
                                    children: DELIVERABLE_TYPE_LABELS[requirement.deliverableType] || '?'
                                }),
                                requirement.isMandatory && /*#__PURE__*/ _jsx(Badge, {
                                    variant: "destructive",
                                    className: "text-xs",
                                    children: "Mandatory"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-gray-700 leading-relaxed",
                            children: truncatedText
                        }),
                        requirement.isEvaluated && requirement.evaluationFactor && /*#__PURE__*/ _jsxs("div", {
                            className: "text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded inline-block",
                            children: [
                                requirement.evaluationFactor,
                                requirement.evaluationSubfactor && ` / ${requirement.evaluationSubfactor}`,
                                requirement.evaluationWeight != null && ` (${requirement.evaluationWeight}%)`
                            ]
                        }),
                        requirement.relatedCLINs && requirement.relatedCLINs.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-1 flex-wrap",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-xs text-gray-500",
                                    children: "CLINs:"
                                }),
                                requirement.relatedCLINs.map((clin)=>/*#__PURE__*/ _jsx(Badge, {
                                        variant: "outline",
                                        className: "text-xs bg-cyan-50 text-cyan-700 border-cyan-200",
                                        children: clin
                                    }, clin))
                            ]
                        }),
                        requirement.entities && requirement.entities.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-1 flex-wrap",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-xs text-gray-500",
                                    children: "Entities:"
                                }),
                                requirement.entities.map((entity)=>/*#__PURE__*/ _jsx(Badge, {
                                        variant: "outline",
                                        className: "text-xs bg-slate-50 text-slate-600 border-slate-200",
                                        children: entity
                                    }, entity))
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2 text-xs text-gray-500",
                            children: [
                                /*#__PURE__*/ _jsx(FileText, {
                                    className: "w-3 h-3"
                                }),
                                requirement.source.filename && /*#__PURE__*/ _jsx("span", {
                                    children: requirement.source.filename
                                }),
                                requirement.source.pageNumber > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        requirement.source.filename && /*#__PURE__*/ _jsx("span", {
                                            children: "•"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "Page ",
                                                requirement.source.pageNumber
                                            ]
                                        })
                                    ]
                                }),
                                requirement.source.sectionNumber && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "•"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "Section ",
                                                requirement.source.sectionNumber
                                            ]
                                        })
                                    ]
                                }),
                                sourcePath && sourcePath !== `Section ${requirement.source.sectionNumber}` && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "•"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: sourcePath
                                        })
                                    ]
                                }),
                                requirement.confidence < 1 && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "•"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "text-amber-600",
                                            children: [
                                                Math.round(requirement.confidence * 100),
                                                "% confidence"
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        requirement.mapping?.proposalSection && /*#__PURE__*/ _jsxs("div", {
                            className: "text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded",
                            children: [
                                "Mapped to: ",
                                requirement.mapping.proposalSection
                            ]
                        })
                    ]
                }),
                displayText.length > 200 && /*#__PURE__*/ _jsx("button", {
                    onClick: (e)=>{
                        e.stopPropagation();
                        handleExpand();
                    },
                    className: "flex-shrink-0 p-1 hover:bg-gray-100 rounded",
                    "aria-label": isExpanded ? 'Collapse' : 'Expand',
                    children: isExpanded ? /*#__PURE__*/ _jsx(ChevronUp, {
                        className: "w-4 h-4 text-gray-500"
                    }) : /*#__PURE__*/ _jsx(ChevronDown, {
                        className: "w-4 h-4 text-gray-500"
                    })
                })
            ]
        })
    });
};
